import { useCallback, useEffect, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import { createInvoiceLine } from "../utils/invoiceReview";

const fields = ["applied_ai_review_id", "supplier_id", "invoice_number", "invoice_date", "currency", "subtotal", "tax", "total_amount", "notes"];
const isObject = (value) => Boolean(value && typeof value === "object" && !Array.isArray(value));
const copy = (value) => JSON.parse(JSON.stringify(value));
const keyFor = (userId, businessUnitId, invoiceId) => `pilcore:invoice-review-draft:v1:${userId}:${businessUnitId}:${invoiceId}`;
const validId = (value) => /^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value));
const sourceOf = (record) => JSON.stringify([record.original_version || null, record.file_hash || null, record.s3_bucket || null, record.s3_key || null, record.original_file_name || null]);
const referenceFields = ["reference", "date", "net_amount", "reason", "confidence", "match_status", "match_reason", "match_message"];
const noteFields = ["id", "delivery_note_number", "delivery_note_date", "currency", "total_amount"];
const scalarFields = (value, keys) => Object.fromEntries(keys.filter((key) => value[key] == null
  || ["string", "number"].includes(typeof value[key])).filter((key) => value[key] !== undefined).map((key) => [key, value[key]]));
const reviewReferencesOf = (record) => {
  const review = record?.applied_ai_review, original = record?.original_version;
  if (!isObject(review) || !original || typeof review.review_id !== "string"
    || review.review_id !== record.applied_ai_review_id
    || (review.original_version != null && review.original_version !== original)
    || !Array.isArray(review.delivery_notes) || review.delivery_notes.length > 500) return null;
  return { review_id: review.review_id, original_version: original,
    ...(Array.isArray(review.invoice_lines) ? { invoice_lines: review.invoice_lines.filter(row => isObject(row) && typeof row.reference === "string")
      .map(row => ({ ...scalarFields(row, ["kind", "reference", "net_amount"]),
        ...(Array.isArray(row.delivery_note_references) ? { delivery_note_references: row.delivery_note_references.filter(ref => typeof ref === "string") } : {}) })) } : {}),
    delivery_notes: review.delivery_notes.filter((entry) => isObject(entry) && typeof entry.reference === "string")
      .map((entry) => ({ ...scalarFields(entry, referenceFields), ...(isObject(entry.matched_delivery_note)
        && validId(entry.matched_delivery_note.id) ? { matched_delivery_note: scalarFields(entry.matched_delivery_note, noteFields) } : {}) })) };
};

// Keep the values exactly as typed, including an incomplete amount. Do not store
// OCR responses or signed URLs. The applied review's reference list is a local
// display snapshot only; its ID and original must still match the saved draft.
export function invoiceReviewDraftOf(record) {
  const draft = Object.fromEntries(fields.map((field) => [field, record?.[field] ?? null]));
  draft.supplier = isObject(record?.supplier) ? copy(record.supplier) : null;
  draft.delivery_notes = Array.isArray(record?.delivery_notes) ? copy(record.delivery_notes) : [];
  if (Array.isArray(record?.invoice_lines)) draft.invoice_lines = copy(record.invoice_lines);
  const review = reviewReferencesOf(record);
  if (review) draft.applied_ai_review = review;
  return draft;
}
const recordFromDraft = (remote, draft) => {
  const record = { ...copy(remote), ...copy(draft) }, review = reviewReferencesOf(record);
  // An older local backup only knew about flat note links. Retain the server's
  // grouping for surviving links when the user explicitly recovers that backup.
  if (Array.isArray(remote.invoice_lines) && !Array.isArray(draft.invoice_lines)) {
    const unassigned = new Set(record.delivery_notes.map(note => Number(note.delivery_note_id)));
    record.invoice_lines = remote.invoice_lines.map(line => ({ ...line, delivery_note_ids: line.delivery_note_ids.filter(id => unassigned.delete(Number(id))) }));
    for (const note of record.delivery_notes) {
      if (unassigned.has(Number(note.delivery_note_id))) record.invoice_lines.push({
        ...createInvoiceLine(note.delivery_note_number || String(note.delivery_note_id)),
        net_amount: note.invoiced_amount ?? null, delivery_note_ids: [Number(note.delivery_note_id)],
      });
    }
  }
  if (review) record.applied_ai_review = review;
  else delete record.applied_ai_review;
  return record;
};

const validDraft = (value) => isObject(value)
  && fields.every((field) => value[field] == null || ["string", "number"].includes(typeof value[field]))
  && (value.supplier === null || isObject(value.supplier))
  && (value.invoice_lines == null || (Array.isArray(value.invoice_lines) && value.invoice_lines.every(line =>
    isObject(line) && typeof line.id === "string" && typeof line.reference === "string" && Array.isArray(line.delivery_note_ids)
    && line.delivery_note_ids.every(validId) && (line.invoice_only == null || typeof line.invoice_only === "boolean")
    && (line.net_amount == null || ["string", "number"].includes(typeof line.net_amount)))))
  && Array.isArray(value.delivery_notes) && value.delivery_notes.every((row) => isObject(row) && validId(row.delivery_note_id));
const textValue = (value) => value == null || value === "" ? null : String(value).trim() || null;
const amountValue = (value) => {
  if (value === "" || value == null) return null;
  return /^-?\d+(?:\.\d+)?$/.test(String(value)) ? Number(value) : String(value);
};
// The API canonicalises blanks and decimal strings. A successful save must not
// leave a phantom recovery copy just because "1.00" was returned as "1.0000".
const comparable = (draft) => JSON.stringify({
  ...Object.fromEntries(fields.map((field) => [field, ["subtotal", "tax", "total_amount"].includes(field)
    ? amountValue(draft[field]) : textValue(draft[field])])),
  invoice_lines: draft.invoice_lines?.map(line => ({ id: line.id, reference: textValue(line.reference), invoice_only: line.invoice_only === true,
    net_amount: amountValue(line.net_amount), delivery_note_ids: line.delivery_note_ids.map(Number) })) ?? null,
  delivery_notes: draft.delivery_notes.map((row) => ({
    delivery_note_id: Number(row.delivery_note_id), invoiced_amount: amountValue(row.invoiced_amount),
    investigation_note: textValue(row.investigation_note),
  })),
});
const empty = { status: "idle", error: null, hasRecovery: false, canRecover: false, dirty: false, updatedAt: null, safeToLeave: true };

/** Browser recovery complements the page's existing server autosave. */
export default function useInvoiceReviewDraft({ businessUnitId, userId, isCurrent, enabled = true }) {
  const session = useRef(null);
  const options = useRef(null);
  const mounted = useRef(true);
  const scopeKey = `${userId}:${businessUnitId}`;
  options.current = { businessUnitId, userId, isCurrent, enabled, scopeKey };
  const [view, setView] = useState({ ...empty, scopeKey });

  const allowed = useCallback(() => {
    const current = options.current, user = readStoredUser();
    return current.enabled && validId(current.userId) && validId(current.businessUnitId)
      && current.isCurrent?.() === true && String(user?.id) === String(current.userId)
      && Number(user?.active_business_unit_id) === Number(current.businessUnitId)
      && (Number(user.id) === 1 || user.permissions?.["purchasing.supplier_invoices.update"] === true);
  }, []);
  const emit = useCallback(() => {
    const s = session.current;
    if (!mounted.current || (s && s.scopeKey !== options.current.scopeKey)) return;
    setView(s ? { scopeKey: s.scopeKey, status: s.status, error: s.error, hasRecovery: Boolean(s.recovery),
      canRecover: Boolean(s.recovery && !s.sourceChanged && !s.readonly && !s.needsReload && allowed()),
      dirty: s.dirty, updatedAt: s.updatedAt, safeToLeave: !s.dirty || s.localJson === JSON.stringify(s.current) }
      : { ...empty, scopeKey: options.current.scopeKey });
  }, [allowed]);
  const currentSession = useCallback(() => {
    const s = session.current;
    if (!s || s.scopeKey !== options.current.scopeKey || !allowed()) return null;
    return s;
  }, [allowed]);
  const records = useCallback((s) => {
    try {
      return Object.keys(localStorage).filter((key) => key === s.key || key.startsWith(`${s.key}:recovery:`))
        .map((key) => {
          try { return { key, record: JSON.parse(localStorage.getItem(key)) }; } catch (_) { return { key, record: null }; }
        }).filter(({ record }) => record?.version === 1 && record.scope === s.scopeKey
          && String(record.invoice_id) === String(s.id) && Number.isSafeInteger(record.revision)
          && record.revision >= 0 && typeof record.source === "string" && validDraft(record.draft))
        .sort((a, b) => String(b.record.updated_at || "").localeCompare(String(a.record.updated_at || "")));
    } catch (_) { return []; }
  }, []);
  const preserve = useCallback((s, record, dismissed = false) => {
    localStorage.setItem(`${s.key}:recovery:${record.writer}`, JSON.stringify({ ...record, dismissed }));
  }, []);
  const backup = useCallback((s) => {
    try {
      const previous = records(s).find(({ key }) => key === s.key)?.record;
      if (previous && previous.writer !== s.writer && JSON.stringify(previous.draft) !== JSON.stringify(s.current)) preserve(s, previous);
      const record = { version: 1, scope: s.scopeKey, invoice_id: s.id, writer: s.writer,
        revision: s.remote.revision, source: sourceOf(s.remote), draft: s.current, updated_at: new Date().toISOString() };
      localStorage.setItem(s.key, JSON.stringify(record));
      s.localJson = JSON.stringify(s.current);
      return true;
    } catch (_) { s.localJson = null; return false; }
  }, [preserve, records]);
  const clearAcknowledged = useCallback((s, saved) => {
    try {
      records(s).forEach(({ key, record }) => {
        if ((record.writer === s.writer && !s.dirty)
          || (record.source === sourceOf(s.remote) && comparable(record.draft) === comparable(saved))) localStorage.removeItem(key);
      });
    } catch (_) { /* The server has already acknowledged these values. */ }
  }, [records]);

  const open = useCallback((serverRecord) => {
    const scope = options.current;
    if (!allowed() || !validId(serverRecord?.id) || !Number.isSafeInteger(serverRecord.revision)
      || serverRecord.revision < 0 || (serverRecord.business_unit_id != null && Number(serverRecord.business_unit_id) !== Number(scope.businessUnitId))) {
      session.current = null; emit();
      return { record: serverRecord, dirty: false, recovered: false, hasRecovery: false, blocked: true };
    }
    const s = { key: keyFor(scope.userId, scope.businessUnitId, serverRecord.id), scopeKey: scope.scopeKey,
      id: serverRecord.id, writer: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      remote: copy(serverRecord), current: invoiceReviewDraftOf(serverRecord), recovery: null,
      readonly: serverRecord.status === "reconciled" || Boolean(serverRecord.resolved_invoice_id), sourceChanged: false, needsReload: false,
      dirty: false, localJson: null, status: "saved", error: null, updatedAt: serverRecord.updated_at || null };
    const saved = s.current;
    const local = records(s).find(({ record }) => !record.dismissed
      && (record.source !== sourceOf(serverRecord) || comparable(record.draft) !== comparable(saved)))?.record;
    let recovered = false;
    if (local) {
      s.sourceChanged = local.source !== sourceOf(serverRecord);
      if (local.revision === serverRecord.revision && !s.readonly && !s.sourceChanged) {
        s.current = invoiceReviewDraftOf(recordFromDraft(s.remote, local.draft)); s.dirty = true; recovered = true;
        const durable = backup(s);
        s.status = durable ? "local" : "error"; s.error = durable ? null : "storage";
      } else {
        s.recovery = local; s.status = "conflict";
        s.error = s.sourceChanged ? "source" : s.readonly ? "readonly" : "revision";
      }
    } else clearAcknowledged(s, saved);
    session.current = s; emit();
    return { record: recordFromDraft(serverRecord, s.current), dirty: s.dirty,
      recovered, hasRecovery: Boolean(s.recovery), blocked: Boolean(s.recovery) };
  }, [allowed, backup, clearAcknowledged, emit, records]);

  const change = useCallback((record) => {
    const s = currentSession();
    if (!s || String(record?.id) !== String(s.id) || sourceOf(record) !== sourceOf(s.remote) || s.recovery || s.readonly || s.needsReload) return false;
    const next = invoiceReviewDraftOf(record);
    if (!validDraft(next)) return false;
    s.current = next; s.dirty = comparable(next) !== comparable(invoiceReviewDraftOf(s.remote));
    s.error = null;
    if (s.dirty) { s.status = backup(s) ? "local" : "error"; if (!s.localJson) s.error = "storage"; }
    else { s.status = "saved"; clearAcknowledged(s, next); s.localJson = null; }
    emit(); return true;
  }, [backup, clearAcknowledged, currentSession, emit]);

  const acknowledge = useCallback((savedRecord, submittedRecord) => {
    const s = currentSession();
    if (!s || String(savedRecord?.id) !== String(s.id) || !Number.isSafeInteger(savedRecord.revision)
      || savedRecord.revision < s.remote.revision || sourceOf(savedRecord) !== sourceOf(s.remote)
      || (savedRecord.business_unit_id != null && Number(savedRecord.business_unit_id) !== Number(options.current.businessUnitId))) return null;
    // A completion from an earlier editor instance must never change the base
    // revision of a newly opened or conflicted draft.
    if (submittedRecord && (String(submittedRecord.id) !== String(s.id) || submittedRecord.revision !== s.remote.revision)) return null;
    if (s.recovery || s.needsReload) return null;
    const submitted = invoiceReviewDraftOf(submittedRecord || s.remote);
    const changedWhileSaving = JSON.stringify(s.current) !== JSON.stringify(submitted);
    const newer = changedWhileSaving ? copy(s.current) : null;
    s.remote = copy(savedRecord); s.readonly = savedRecord.status === "reconciled" || Boolean(savedRecord.resolved_invoice_id);
    s.current = newer || invoiceReviewDraftOf(savedRecord);
    s.dirty = comparable(s.current) !== comparable(invoiceReviewDraftOf(savedRecord));
    s.updatedAt = savedRecord.updated_at || new Date().toISOString(); s.error = null;
    clearAcknowledged(s, invoiceReviewDraftOf(savedRecord));
    if (s.dirty) { s.status = backup(s) ? "local" : "error"; if (!s.localJson) s.error = "storage"; }
    else { s.status = "saved"; s.localJson = null; }
    emit(); return { record: recordFromDraft(savedRecord, s.current), dirty: s.dirty };
  }, [backup, clearAcknowledged, currentSession, emit]);

  const failed = useCallback((failure) => {
    const s = currentSession(); if (!s) return;
    const code = failure?.response?.data?.code || failure?.code;
    const conflict = ["SUPPLIER_INVOICE_REVISION_CONFLICT", "SUPPLIER_INVOICE_REVISION_REQUIRED", "SUPPLIER_INVOICE_CONFLICT"].includes(code);
    const unavailable = [403, 404].includes(failure?.response?.status) || code === "SUPPLIER_INVOICE_BUSINESS_UNIT_CHANGED";
    s.needsReload = conflict || unavailable;
    s.status = conflict ? "conflict" : unavailable ? "blocked" : s.localJson ? "local" : "error";
    s.error = conflict ? "revision" : unavailable ? "scope" : "save";
    emit();
  }, [currentSession, emit]);
  const recover = useCallback(() => {
    const s = currentSession();
    if (!s?.recovery || s.needsReload || s.sourceChanged || s.readonly) return null;
    s.current = invoiceReviewDraftOf(recordFromDraft(s.remote, s.recovery.draft)); s.recovery = null;
    s.dirty = comparable(s.current) !== comparable(invoiceReviewDraftOf(s.remote));
    const durable = s.dirty ? backup(s) : true;
    s.status = s.dirty ? durable ? "local" : "error" : "saved"; s.error = durable ? null : "storage";
    emit(); return { record: recordFromDraft(s.remote, s.current), dirty: s.dirty };
  }, [backup, currentSession, emit]);
  const useSaved = useCallback(() => {
    const s = currentSession(); if (!s || s.needsReload || !s.recovery) return null;
    try {
      records(s).filter(({ record }) => !record.dismissed).forEach(({ key, record }) => {
        preserve(s, record, true); if (key === s.key) localStorage.removeItem(key);
      });
      records(s).filter(({ record }) => record.dismissed).slice(3).forEach(({ key }) => localStorage.removeItem(key));
    } catch (_) { s.status = "error"; s.error = "storage"; emit(); return null; }
    s.current = invoiceReviewDraftOf(s.remote); s.recovery = null; s.dirty = false; s.localJson = null;
    s.status = "saved"; s.error = null; emit();
    return { record: recordFromDraft(s.remote, s.current), dirty: false };
  }, [currentSession, emit, preserve, records]);
  const close = useCallback(() => { session.current = null; emit(); }, [emit]);
  const forget = useCallback((invoiceId) => {
    if (!allowed() || !validId(invoiceId)) return;
    const key = keyFor(options.current.userId, options.current.businessUnitId, invoiceId);
    try { Object.keys(localStorage).filter((entry) => entry === key || entry.startsWith(`${key}:recovery:`)).forEach((entry) => localStorage.removeItem(entry)); }
    catch (_) { /* A deleted invoice cannot be reopened from its local backup. */ }
    if (String(session.current?.id) === String(invoiceId)) close();
  }, [allowed, close]);
  const canLeave = useCallback(() => {
    const s = session.current;
    return !s || !s.dirty || s.localJson === JSON.stringify(s.current);
  }, []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  return { ...(view.scopeKey === scopeKey && enabled ? view : empty), open, change, acknowledge, failed, recover, useSaved, close, forget, canLeave };
}
